/**
 * useToolExecution Hook
 * Manages tool execution and result interpretation
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { streamMessage } from "../services/aiChatService";
import {
        CHAT_TOOLS,
        executeChatTool,
        KPI_FIELD_CONFIG,
        formatDateLabel,
        formatKpiValue,
        resolveKpiLabel,
        summarizeKpiMetric,
        formatPeriodType,
        resolvePrimaryKpi,
} from "../services/chatToolsService";
import { saveMessage } from "../components/chatdialog/utils/firestoreUtils";
import { getDb } from "../components/chatdialog/utils/firestoreUtils";
import {
        sanitizeAiResponse,
        hasDisplayableContent,
        stripToolInvocationSyntax,
} from "../components/chatdialog/utils/messageUtils";
import { extractToolCalls } from "../components/chatdialog/utils/toolParsing";

const MAX_INTERPRETATION_CYCLES = 2;
const DETAIL_TOKEN_REGEX = /[\d%•\n-]/;

const needsSummaryFallback = (text) => {
        if (!text) {
                return true;
        }

        const trimmed = text.trim();

        if (!trimmed) {
                return true;
        }

        if (DETAIL_TOKEN_REGEX.test(trimmed)) {
                return false;
        }

        return trimmed.length < 90;
};

const formatDateOrFallback = (value, locale, t) => {
        if (!value) {
                return t("ai-chat-field-date-not-set");
        }

        try {
                return formatDateLabel(value, locale, t);
        } catch (error) {
                console.warn("[useToolExecution] - Failed to format date value", value, error);
                return String(value);
        }
};

const extractMetricValue = (metric, key) => {
        if (!metric || !key) {
                return null;
        }

        if (Object.prototype.hasOwnProperty.call(metric, key)) {
                return metric[key];
        }

        if (Object.prototype.hasOwnProperty.call(metric, "value")) {
                return metric.value;
        }

        return null;
};

const buildKpiTrendFallback = (metadata = {}, locale, t) => {
        const points = Array.isArray(metadata.points) ? metadata.points : [];
        if (points.length === 0) {
                return null;
        }

        const latestPoint = points[points.length - 1];
        const firstPoint = points[0];
        const candidateKey = resolvePrimaryKpi(latestPoint) || resolvePrimaryKpi(firstPoint) || metadata.kpi;
        const valueKey = candidateKey || (metadata.kpi || "value");
        const kpiLabel = resolveKpiLabel(metadata.kpi || valueKey, t);

        const lines = [
                `📈 ${t("ai-chat-tool-farm-kpi-trends-title")} • ${kpiLabel}`,
        ];

        if (metadata.query?.periodType) {
                lines.push(`${t("ai-chat-field-period-type")}: ${formatPeriodType(metadata.query.periodType, t)}`);
        }

        if (metadata.query?.startDate || metadata.query?.endDate) {
                const start = formatDateOrFallback(metadata.query?.startDate, locale, t);
                const end = formatDateOrFallback(metadata.query?.endDate, locale, t);
                lines.push(`${t("ai-chat-field-date-range")}: ${start} → ${end}`);
        }

        if (metadata.change) {
                lines.push(metadata.change);
        }

        if (points.length > 1 && firstPoint) {
                const startValue = extractMetricValue(firstPoint, valueKey);
                if (startValue !== null && startValue !== undefined) {
                        const formattedStart = formatKpiValue(valueKey, startValue, locale, t);
                        const startDate = firstPoint.date ? formatDateLabel(firstPoint.date, locale, t) : null;
                        lines.push(`${t("ai-chat-field-start")}: ${startDate || t("ai-chat-field-date-not-set")} (${formattedStart})`);
                }
        }

        const latestValue = extractMetricValue(latestPoint, valueKey);
        if (latestValue !== null && latestValue !== undefined) {
                const formattedLatest = formatKpiValue(valueKey, latestValue, locale, t);
                const latestDate = latestPoint.date ? formatDateLabel(latestPoint.date, locale, t) : null;
                lines.push(`${t("ai-chat-field-end")}: ${latestDate || t("ai-chat-field-date-not-set")} (${formattedLatest})`);
        }

        return lines.filter(Boolean).join("\n");
};

const buildKpiOverviewFallback = (metadata = {}, locale, t) => {
        const latestMetric = metadata.latest;
        if (!latestMetric || typeof latestMetric !== "object") {
                return null;
        }

        const lines = [`📊 ${t("ai-chat-tool-farm-kpi-overview-title")}`];

        const summaryLine = summarizeKpiMetric(latestMetric, locale, t);
        if (summaryLine) {
                lines.push(summaryLine);
        }

        const metricLines = KPI_FIELD_CONFIG
                .filter((entry) => latestMetric[entry.key] !== undefined && latestMetric[entry.key] !== null)
                .slice(0, 3)
                .map((entry) => `${t(entry.translationKey)}: ${formatKpiValue(entry.key, latestMetric[entry.key], locale, t)}`);

        metricLines.forEach((metricLine) => {
                lines.push(`• ${metricLine}`);
        });

        return lines.filter(Boolean).join("\n");
};

const buildFallbackSummary = (history, locale, t) => {
        if (!Array.isArray(history) || history.length === 0) {
                return null;
        }

        const latestToolMessage = [...history]
                .reverse()
                .find((message) => message?.metadata?.type === "tool-result" && message.metadata?.toolId);

        if (!latestToolMessage) {
                return null;
        }

        const { toolId, ...metadata } = latestToolMessage.metadata || {};

        switch (toolId) {
                case "farm-kpi-trends":
                        return buildKpiTrendFallback(metadata, locale, t);
                case "farm-kpi-overview":
                        return buildKpiOverviewFallback(metadata, locale, t);
                default:
                        return null;
        }
};

/**
 * Hook to manage tool execution and interpretation
 * @param {string} userId - User ID
 * @param {string} chatId - Current chat session ID
 * @param {Function} ensureChatSession - Function to ensure chat session exists
 * @param {Function} refreshUserContext - Function to refresh user context
 * @param {Object} userContext - Current user context
 * @param {Function} enhanceResponse - Function to enhance AI responses
 * @returns {Object} { runTool, toolLoadingId, toolError, interpretToolResults }
 */
export const useToolExecution = (
        userId,
        chatId,
        ensureChatSession,
        refreshUserContext,
        userContext,
        enhanceResponse
) => {
        const { t, i18n } = useTranslation();
        const navigate = useNavigate();
        const [toolLoadingId, setToolLoadingId] = useState(null);
        const [toolError, setToolError] = useState(null);
        
        const runToolRef = useRef(null);
        const db = getDb();

        /**
         * Interpret tool results using AI
         * @param {number} interpretationCycle - Current interpretation cycle
         * @returns {Promise<void>}
         */
        const interpretToolResults = useCallback(async (interpretationCycle = 0) => {
                if (interpretationCycle >= MAX_INTERPRETATION_CYCLES) {
                        return;
                }

                if (!userId) {
                        return;
                }

                const activeChatId = await ensureChatSession();
                if (!activeChatId) {
                        return;
                }

                try {
                        // Fetch latest messages from Firestore to ensure we have up-to-date history
                        const messagesSnapshot = await db
                                .collection("Users")
                                .doc(userId)
                                .collection("AIChat")
                                .doc(activeChatId)
                                .collection("Messages")
                                .orderBy("timestamp", "asc")
                                .get();

                        const latestHistory = messagesSnapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                        }));

                        // Refresh context before interpretation
                        let context = userContext;
                        try {
                                const refreshedContext = await refreshUserContext();
                                if (refreshedContext) {
                                        context = refreshedContext;
                                }
                        } catch (contextError) {
                                console.error(
                                        "[useToolExecution][interpretToolResults] - Error refreshing user context:",
                                        contextError
                                );
                        }

                        // Create interpretation prompt
                        const interpretationPrompt = interpretationCycle === 0
                                ? t("ai-chat-interpret-tool-results") || "Please analyze and interpret the tool results shown above. Summarize the key insights and provide helpful recommendations. If additional data is needed for a comprehensive analysis, use the appropriate tools."
                                : t("ai-chat-interpret-new-results") || "Please analyze and interpret the newly added tool results in the context of the previous data. Provide a comprehensive analysis and recommendations.";

                        // Stream AI interpretation
                        let fullResponse = "";
                        await streamMessage(
                                userId,
                                interpretationPrompt,
                                context,
                                latestHistory,
                                (chunk) => {
                                        fullResponse += chunk;
                                },
                                { language: i18n.language }
                        );

                        const toolCalls = extractToolCalls(fullResponse);
                        const uniqueToolCallsMap = new Map();

                        toolCalls.forEach((call) => {
                                const payloadKey =
                                        call.payload && typeof call.payload === "object"
                                                ? JSON.stringify(call.payload)
                                                : String(call.payload ?? "");
                                const key = `${call.id}|${payloadKey}`;

                                if (!uniqueToolCallsMap.has(key)) {
                                        uniqueToolCallsMap.set(key, call);
                                }
                        });

                        const uniqueToolCalls = Array.from(uniqueToolCallsMap.values());
                        const fallbackSummary = buildFallbackSummary(latestHistory, i18n.language, t);
                        let sanitizedResponse = stripToolInvocationSyntax(sanitizeAiResponse(fullResponse));
                        let sanitizedHasContent = hasDisplayableContent(sanitizedResponse);

                        if (!sanitizedHasContent && fallbackSummary) {
                                sanitizedResponse = fallbackSummary;
                                sanitizedHasContent = hasDisplayableContent(sanitizedResponse);
                        }

                        if (!sanitizedHasContent && uniqueToolCalls.length > 0) {
                                const primaryTool = CHAT_TOOLS[uniqueToolCalls[0].id];
                                sanitizedResponse = t("ai-chat-tool-auto-fetching", {
                                        tool: primaryTool ? t(primaryTool.labelKey) : uniqueToolCalls[0].id,
                                });
                                sanitizedHasContent = hasDisplayableContent(sanitizedResponse);
                        }

                        if (
                                sanitizedHasContent &&
                                needsSummaryFallback(sanitizedResponse) &&
                                fallbackSummary &&
                                fallbackSummary !== sanitizedResponse
                        ) {
                                sanitizedResponse = fallbackSummary;
                                sanitizedHasContent = hasDisplayableContent(sanitizedResponse);
                        }

                        // Enhance response with animal IDs if it mentions listing suspicious animals
                        if (sanitizedHasContent && enhanceResponse) {
                                try {
                                        sanitizedResponse = await enhanceResponse(sanitizedResponse);
                                } catch (enhanceError) {
                                        console.error("[useToolExecution][interpretToolResults] - Error enhancing response:", enhanceError);
                                        // Continue with original response if enhancement fails
                                }
                                sanitizedHasContent = hasDisplayableContent(sanitizedResponse);
                        }

                        // Check if this will be the final interpretation (no more tool calls or max cycles reached)
                        const willBeFinal = uniqueToolCalls.length === 0 || (interpretationCycle + 1 >= MAX_INTERPRETATION_CYCLES);

                        if (sanitizedHasContent) {
                                await saveMessage(userId, activeChatId, sanitizedResponse, "ai", {
                                        type: "ai-interpretation",
                                        toolCalls: uniqueToolCalls,
                                        interpretationCycle,
                                        isFinal: willBeFinal,
                                });
                        }

                        // If AI called more tools, execute them and interpret again
                        if (uniqueToolCalls.length > 0 && (interpretationCycle + 1 < MAX_INTERPRETATION_CYCLES)) {
                                const runToolFn = runToolRef.current;

                                if (runToolFn) {
                                        for (const call of uniqueToolCalls) {
                                                await runToolFn(call.id, {
                                                        skipUserMessage: true,
                                                        suppressLoader: true,
                                                        source: "auto",
                                                        payload: call.payload,
                                                });
                                        }
                                } else {
                                        console.warn("[useToolExecution][interpretToolResults] - runTool reference is not available");
                                }

                                // Recursively call interpretation for the new tool results
                                await interpretToolResults(interpretationCycle + 1);
                        }
                } catch (error) {
                        console.error("[useToolExecution][interpretToolResults] - Error interpreting tool results:", error);
                }
        }, [userId, ensureChatSession, userContext, refreshUserContext, t, i18n.language, enhanceResponse, db]);

        /**
         * Execute a chat tool
         * @param {string} toolId - Tool ID to execute
         * @param {Object} options - Tool execution options
         * @returns {Promise<Object|null>} Tool result or null
         */
        const runTool = useCallback(
                async (toolId, options = {}) => {
                        const {
                                customLabel,
                                skipUserMessage = false,
                                suppressLoader = false,
                                source = "quick-action",
                                payload = null,
                        } = options;

                        if (!userId) {
                                return null;
                        }

                        const activeChatId = await ensureChatSession();

                        if (!activeChatId) {
                                return null;
                        }

                        const tool = CHAT_TOOLS[toolId];

                        if (!tool) {
                                console.warn(`[useToolExecution][runTool] - Unknown tool: ${toolId}`);
                                return null;
                        }

                        const labelText = customLabel || t(tool.labelKey);

                        if (!suppressLoader) {
                                setToolLoadingId(toolId);
                        }

                        setToolError(null);

                        try {
                                if (!skipUserMessage) {
                                        await saveMessage(
                                                userId,
                                                activeChatId,
                                                t("ai-chat-tool-user-request", { tool: labelText }),
                                                "user",
                                                {
                                                        toolId,
                                                        source,
                                                        type: "tool-request",
                                                },
                                        );
                                }

                                const result = await executeChatTool(toolId, userId, t, i18n.language, {
                                        payload,
                                        source,
                                });

                                const navigationData = result?.metadata?.navigation;
                                const isNavigationTool = toolId === "navigate-page" || toolId === "navigate-animal";
                                const navigationSucceeded = navigationData?.path !== undefined;

                                // Skip saving success messages for navigation tools when navigation succeeds
                                // Still save messages for errors or when navigation fails
                                if (result?.message && !(isNavigationTool && navigationSucceeded)) {
                                        await saveMessage(
                                                userId,
                                                activeChatId,
                                                result.message,
                                                "ai",
                                                {
                                                        ...(result.metadata || {}),
                                                        toolId,
                                                        source,
                                                        type: "tool-result",
                                                },
                                        );
                                }

                                if (navigationData?.path) {
                                        const navigationOptions = {};

                                        if (
                                                Object.prototype.hasOwnProperty.call(navigationData, "state") &&
                                                navigationData.state !== undefined
                                        ) {
                                                navigationOptions.state = navigationData.state;
                                        }

                                        if (navigationData.replace) {
                                                navigationOptions.replace = true;
                                        }

                                        try {
                                                navigate(navigationData.path, navigationOptions);
                                        } catch (navigationError) {
                                                console.error(
                                                        "[useToolExecution][runTool] - Error navigating to path:",
                                                        navigationData.path,
                                                        navigationError,
                                                );
                                        }
                                }

                                // Automatically trigger analysis for quick actions (skip for navigation tools)
                                if (source === "quick-action" && result?.message && !isNavigationTool) {
                                        try {
                                                await interpretToolResults(0);
                                        } catch (interpretError) {
                                                console.error(
                                                        "[useToolExecution][runTool] - Error interpreting tool results:",
                                                        interpretError,
                                                );
                                                // Don't throw - allow tool execution to succeed even if interpretation fails
                                        }
                                }

                                return result;
                        } catch (toolErrorInstance) {
                                console.error(
                                        `[useToolExecution][runTool] - Error executing tool ${toolId}:`,
                                        toolErrorInstance,
                                );
                                const errorMessage = t("ai-chat-tool-error-message", { tool: labelText });
                                setToolError(errorMessage);

                                await saveMessage(
                                        userId,
                                        activeChatId,
                                        errorMessage,
                                        "ai",
                                        {
                                                toolId,
                                                source,
                                                type: "tool-error",
                                        },
                                );

                                return null;
                        } finally {
                                if (!suppressLoader) {
                                        setToolLoadingId(null);
                                }
                        }
                },
                [userId, ensureChatSession, t, i18n.language, navigate, interpretToolResults],
        );

        // Keep a ref to the latest runTool implementation for interpretToolResults
        useEffect(() => {
                runToolRef.current = runTool;
        }, [runTool]);

        return {
                runTool,
                toolLoadingId,
                toolError,
                setToolError,
                interpretToolResults,
        };
};

